import { useEffect, useState } from 'react';
import { api, type UserProfile } from '../api-client.js';
import { getBotDisplayName, isBotUserId } from '../bots/bot-display.js';

/**
 * Process-wide cache so the same user id is fetched once per session.
 * Hooks into useState so React rerenders when a fetch completes.
 */
const cache = new Map<string, UserProfile | null | Promise<UserProfile | null>>();
const subscribers = new Set<() => void>();

function notify(): void {
  for (const cb of subscribers) cb();
}

function fetchAndStore(userId: string): Promise<UserProfile | null> {
  const promise = api
    .getUserProfile(userId)
    .then((profile) => {
      cache.set(userId, profile);
      notify();
      return profile;
    })
    .catch(() => {
      // Cache the miss as null so we don't keep retrying for an
      // unknown id. The user can refresh to retry.
      cache.set(userId, null);
      notify();
      return null;
    });
  cache.set(userId, promise);
  return promise;
}

/**
 * Returns the user's profile (or null if missing). Returns undefined
 * while the first fetch is in flight. Multiple components requesting
 * the same userId share the same fetch.
 */
export function useUserProfile(userId: string | null | undefined): UserProfile | null | undefined {
  const [, force] = useState(0);

  useEffect(() => {
    const cb = () => force((n) => n + 1);
    subscribers.add(cb);
    return () => {
      subscribers.delete(cb);
    };
  }, []);

  if (!userId) return null;
  const cached = cache.get(userId);
  if (cached === undefined) {
    fetchAndStore(userId);
    return undefined;
  }
  if (cached instanceof Promise) return undefined;
  return cached;
}

/**
 * Returns the displayName for a user, falling back to a short prefix
 * of the userId while loading or if the lookup fails. Convenient for
 * places that just want a label.
 */
export function useUserDisplayName(userId: string | null | undefined): string {
  // Bot users aren't in the Prisma users table — short-circuit
  // before the (404-ing) profile fetch.
  const botName = userId && isBotUserId(userId) ? getBotDisplayName(userId) : undefined;
  const profile = useUserProfile(botName ? null : userId);
  if (botName) return botName;
  if (!userId) return '—';
  if (profile === undefined) return userId.slice(0, 8) + '…';
  if (profile === null) return userId.slice(0, 8) + '…';
  return profile.displayName;
}
